import { formatTimestamp } from "../utils";
import {
  YoutubeSourceLocation,
  YouTubeTranscriptLineView,
} from "../api-generated";
import Blob from "./Blob";

interface PlayingIndexIsh {
  lineIndex: number;
  tokenIndeces: number[];
}

interface Props {
  appLocation: YoutubeSourceLocation;
  lines: YouTubeTranscriptLineView[];
  playingLineIshes?: PlayingIndexIsh[];
  focusedLineIndex?: number;
  focusedTokenIndex?: number;
  isFetching: boolean;
  onLineTsClick: (ts: number, lineIndex: number) => void;
  onTokenHover: (lineIndex: number, tokenIndex: number) => void;
  onToggleMultiSelect: (param: {
    lineIndex: number;
    tokenIndex: number;
  }) => void;
  tokensInMultiSelect: number[][];
}

const YouTubeLines: React.FC<Props> = ({
  appLocation,
  lines,
  playingLineIshes,
  focusedLineIndex,
  focusedTokenIndex,
  isFetching,
  onLineTsClick,
  onTokenHover,
  tokensInMultiSelect,
  onToggleMultiSelect,
}) => {
  if (isFetching) {
    return (
      <div className="w-full">
        {Array(8)
          .fill(null)
          .map((_, index) => {
            // NB: between 30 and 90?
            const perc = ((index * 26571) % 61) + 30;
            return (
              <div key={index} className="p-4 bg-white flex gap-8">
                <div
                  className={`bg-gray-300 animate-pulse h-6`}
                  //NB: have to pass width here cuz dynamically generating
                  //the percentage number doesn't work in tailwind:
                  //https://chatgpt.com/c/6716786e-6180-8006-8415-839baa53a246
                  style={{ width: `${perc}%` }}
                />
              </div>
            );
          })}
      </div>
    );
  }

  return (
    <div className="flex-grow justify-between gap-4 pb-4">
      <div className="flex gap-2 justify-start h-full">
        <div>
          {lines.map((line, lineIndex) => {
            let lineTimestamp = line.timeStamp;
            if (lineTimestamp === null) {
              lineTimestamp = line.tokens.find(
                (t) => t.timeStamp !== null
              )?.timeStamp;
            }
            const playingTokenIndeces: number[] | undefined =
              playingLineIshes?.find(
                (ish) => ish.lineIndex === lineIndex
              )?.tokenIndeces;

            const lineLocation: YoutubeSourceLocation = {
              ...appLocation,
              lineIndex,
            };
            return (
              <div
                key={lineIndex}
                //NB: not using line timestamp as key since there can be multiple lines with the same timestamp
                //e.g. 5aimbGCecwY
              >
                <div className="flex gap-2 justify-start max-w-[850px]">
                  <div className="flex flex-col justify-start pt-2">
                    <button
                      className="active:text-fuchsia-700 active:font-bold text-sm min-w-[40px]"
                      onClick={() => {
                        if (lineTimestamp !== undefined) {
                          onLineTsClick(lineTimestamp, lineIndex);
                        }
                      }}
                    >
                      {formatTimestamp(lineTimestamp)}
                    </button>
                  </div>

                  <div>
                    <div>
                      <Blob
                        location={lineLocation}
                        blob={{
                          tokens: line.tokens.map((t) => t.token),
                          englishSentenceTranslation: line.englishTranslation,
                        }}
                        playingTokenIndeces={playingTokenIndeces}
                        focusedTokenIndex={
                          lineIndex === focusedLineIndex
                            ? focusedTokenIndex
                            : undefined
                        }
                        onTokenHover={(tokenI) =>
                          onTokenHover(lineIndex, tokenI)
                        }
                        tokensInMultiSelect={tokensInMultiSelect[lineIndex]}
                        onTokenClick={(_, tokenIndex) =>
                          onToggleMultiSelect({ lineIndex, tokenIndex })
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default YouTubeLines;
